import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { StationTrends } from "../components/StationTrends";

interface Location { id: string; name: string; address: string; type: string; }
interface Station { id: string; code: string; type: string; position_description: string | null; }

export function ClientDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<any>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showStationForm, setShowStationForm] = useState(false);
  const [selectedStation, setSelectedStation] = useState<string | null>(null);
  const [trendData, setTrendData] = useState<any>(null);
  const [locationForm, setLocationForm] = useState({ name: "", address: "", type: "other" });
  const [stationForm, setStationForm] = useState({ code: "", type: "bait_station", positionDescription: "" });
  const [confirmingStationDelete, setConfirmingStationDelete] = useState<string | null>(null);
  const [confirmingLocationDelete, setConfirmingLocationDelete] = useState<string | null>(null);
  const [locationDeleteError, setLocationDeleteError] = useState<string | null>(null);

  const loadClient = async () => { setClient(await api(`/clients/${id}`)); };
  const loadLocations = async () => { setLocations(await api(`/locations?clientId=${id}`)); };
  const loadStations = async (locId: string) => { setStations(await api(`/stations?locationId=${locId}`)); };

  useEffect(() => { loadClient(); loadLocations(); }, [id]);
  useEffect(() => { if (selectedLocation) loadStations(selectedLocation); }, [selectedLocation]);

  const handleAddLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    await api("/locations", { method: "POST", body: JSON.stringify({ clientId: id, ...locationForm }) });
    setShowLocationForm(false);
    setLocationForm({ name: "", address: "", type: "other" });
    loadLocations();
  };

  const handleAddStation = async (e: React.FormEvent) => {
    e.preventDefault();
    await api("/stations", { method: "POST", body: JSON.stringify({ locationId: selectedLocation, ...stationForm }) });
    setShowStationForm(false);
    setStationForm({ code: "", type: "bait_station", positionDescription: "" });
    if (selectedLocation) loadStations(selectedLocation);
  };

  const handleDeleteStation = async (stationId: string) => {
    await api(`/stations/${stationId}`, { method: "DELETE" });
    setConfirmingStationDelete(null);
    if (selectedLocation) loadStations(selectedLocation);
  };

  const handleDeleteLocation = async (locId: string) => {
    setLocationDeleteError(null);
    try {
      await api(`/locations/${locId}`, { method: "DELETE" });
      setConfirmingLocationDelete(null);
      if (selectedLocation === locId) {
        setSelectedLocation(null);
        setStations([]);
      }
      loadLocations();
    } catch (e: any) {
      if (e?.message === "hasActiveJobs") {
        setLocationDeleteError(locId);
      }
    }
  };

  if (!client) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-navy-200 border-t-amber-500 rounded-full animate-spin" />
    </div>
  );

  const locationTypes = ["restaurant", "warehouse", "hotel", "office", "residential", "production", "other"];
  const stationTypes = ["bait_station", "trap", "pheromone_trap", "uv_trap", "glue_board"];

  return (
    <div className="animate-fade-in">
      <Link to="/app/clients" className="inline-flex items-center gap-1.5 text-sm text-navy-400 hover:text-navy-600 transition-colors mb-6">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        {t("common.back")}
      </Link>

      {/* Client header */}
      <div className="bg-white rounded-xl border border-navy-100 p-6 mb-6">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>{client.name}</h1>
        <p className="text-sm text-navy-400 mt-1">
          {[client.address, client.phone, client.email].filter(Boolean).join(" · ") || t("clients.noContactDetails")}
        </p>
        {client.tax_id && <p className="text-xs text-navy-300 mt-1 font-mono">NIP: {client.tax_id}</p>}
      </div>

      {/* Locations */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold" style={{ fontFamily: "var(--font-display)" }}>{t("clients.locations")}</h2>
        <button onClick={() => setShowLocationForm(true)} className="btn btn-primary btn-sm">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {t("clients.addLocation")}
        </button>
      </div>

      {showLocationForm && (
        <form onSubmit={handleAddLocation} className="bg-white rounded-xl border border-navy-100 p-5 mb-4 animate-slide-in">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="input-label">{t("locations.name")} *</label>
              <input value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} required className="input" />
            </div>
            <div>
              <label className="input-label">{t("locations.address")} *</label>
              <input value={locationForm.address} onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })} required className="input" />
            </div>
            <div>
              <label className="input-label">{t("locations.type")}</label>
              <select value={locationForm.type} onChange={(e) => setLocationForm({ ...locationForm, type: e.target.value })} className="input">
                {locationTypes.map((type) => <option key={type} value={type}>{t(`locations.types.${type}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn btn-primary btn-sm">{t("common.save")}</button>
            <button type="button" onClick={() => setShowLocationForm(false)} className="btn btn-ghost btn-sm">{t("common.cancel")}</button>
          </div>
        </form>
      )}

      {locations.length === 0 ? (
        <p className="text-navy-400 text-sm bg-white rounded-xl border border-navy-100 p-8 text-center">{t("clients.noLocations")}</p>
      ) : (
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden mb-6">
          {locations.map((loc, i) => (
            <div
              key={loc.id}
              className={`px-5 py-4 transition-colors ${
                selectedLocation === loc.id ? "bg-amber-50/50 border-l-3 border-l-amber-500" : ""
              } ${i < locations.length - 1 ? "border-b border-navy-50" : ""}`}
            >
              {confirmingLocationDelete === loc.id ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-navy-600">{t("common.confirm")}</p>
                    {locationDeleteError === loc.id && (
                      <p className="text-xs text-rose-500 mt-1">{t("locations.cannotDeleteActiveJobs")}</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => handleDeleteLocation(loc.id)} className="btn btn-danger btn-sm">{t("common.delete")}</button>
                    <button onClick={() => { setConfirmingLocationDelete(null); setLocationDeleteError(null); }} className="btn btn-ghost btn-sm">{t("common.cancel")}</button>
                  </div>
                </div>
              ) : (
                <div
                  className="group flex items-center justify-between cursor-pointer"
                  onClick={() => setSelectedLocation(selectedLocation === loc.id ? null : loc.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      selectedLocation === loc.id ? "bg-amber-100 text-amber-600" : "bg-navy-50 text-navy-400"
                    }`}>
                      <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-navy-900">{loc.name}</p>
                      <p className="text-xs text-navy-400">{t(`locations.types.${loc.type}`)} · {loc.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={(e) => { e.stopPropagation(); setLocationDeleteError(null); setConfirmingLocationDelete(loc.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity text-navy-300 hover:text-rose-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <svg className={`w-4 h-4 text-navy-300 transition-transform ${selectedLocation === loc.id ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Monitoring Stations */}
      {selectedLocation && (
        <div className="animate-slide-in">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ fontFamily: "var(--font-display)" }}>{t("locations.stations")}</h2>
            <div className="flex gap-2">
              {stations.length > 0 && (
                <Link to={`/app/qr/${selectedLocation}`} className="btn btn-ghost btn-sm">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
                  </svg>
                  {t("locations.printQR")}
                </Link>
              )}
              <button onClick={() => setShowStationForm(true)} className="btn btn-primary btn-sm">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                {t("locations.addStation")}
              </button>
            </div>
          </div>

          {showStationForm && (
            <form onSubmit={handleAddStation} className="bg-white rounded-xl border border-navy-100 p-5 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="input-label">{t("locations.stationCode")} *</label>
                  <input value={stationForm.code} onChange={(e) => setStationForm({ ...stationForm, code: e.target.value })} required className="input font-mono" placeholder="S1" />
                </div>
                <div>
                  <label className="input-label">{t("locations.stationType")}</label>
                  <select value={stationForm.type} onChange={(e) => setStationForm({ ...stationForm, type: e.target.value })} className="input">
                    {stationTypes.map((type) => <option key={type} value={type}>{t(`locations.stationTypes.${type}`)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="input-label">{t("locations.stationPosition")}</label>
                  <input value={stationForm.positionDescription} onChange={(e) => setStationForm({ ...stationForm, positionDescription: e.target.value })} className="input" placeholder={t("locations.positionPlaceholder")} />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" className="btn btn-primary btn-sm">{t("common.save")}</button>
                <button type="button" onClick={() => setShowStationForm(false)} className="btn btn-ghost btn-sm">{t("common.cancel")}</button>
              </div>
            </form>
          )}

          {stations.length === 0 ? (
            <p className="text-navy-400 text-sm bg-white rounded-xl border border-navy-100 p-8 text-center">{t("locations.noStations")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stations.map((station) => (
                <div
                  key={station.id}
                  className={`group bg-white rounded-xl border p-4 card-lift cursor-pointer transition-colors ${
                    selectedStation === station.id ? "border-amber-400 ring-1 ring-amber-200" : "border-navy-100"
                  }`}
                  onClick={async () => {
                    if (selectedStation === station.id) {
                      setSelectedStation(null);
                      setTrendData(null);
                    } else {
                      setSelectedStation(station.id);
                      setTrendData(null);
                      try {
                        const data = await api(`/stations/${station.id}/trends`);
                        setTrendData(data);
                      } catch { setTrendData(null); }
                    }
                  }}
                >
                  {confirmingStationDelete === station.id ? (
                    <div className="flex items-center justify-between gap-3" onClick={(e) => e.stopPropagation()}>
                      <p className="text-sm text-navy-600">{t("common.confirm")}</p>
                      <div className="flex gap-2">
                        <button onClick={() => handleDeleteStation(station.id)} className="btn btn-danger btn-sm">{t("common.delete")}</button>
                        <button onClick={() => setConfirmingStationDelete(null)} className="btn btn-ghost btn-sm">{t("common.cancel")}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-navy-900 rounded-lg flex items-center justify-center">
                          <span className="text-amber-400 font-mono text-sm font-bold">{station.code}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium capitalize">{t(`locations.stationTypes.${station.type}`)}</p>
                          <p className="text-xs text-navy-400">{station.position_description || t("locations.noPositionSet")}</p>
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setConfirmingStationDelete(station.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity text-navy-300 hover:text-rose-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Station Trends */}
          {selectedStation && (
            <div className="mt-4 animate-slide-in">
              {trendData ? (
                <StationTrends data={trendData} />
              ) : (
                <div className="bg-white rounded-xl border border-navy-100 p-8 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-navy-200 border-t-amber-500 rounded-full animate-spin" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
